import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Wine, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Layout({ children, transparentNav = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const dashboardLink = () => {
        if (!user) return null;
        if (user.role === "admin") return "/admin";
        if (user.role === "winery") return "/dashboard";
        return null;
    };

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-black text-white flex flex-col">
            <header
                className={`${transparentNav ? "absolute" : "relative bg-black border-b border-[#27272A]"} top-0 left-0 right-0 z-40`}
                data-testid="main-nav"
            >
                <div className="px-6 md:px-12 lg:px-16 py-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                        <Wine className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                        <span className="font-display text-2xl tracking-wide">CellarGlobal</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-10 text-sm">
                        <NavLink to="/wines" data-testid="nav-wines" className={({isActive}) => `tracking-wide transition-colors ${isActive ? "text-[#C9A961]" : "hover:text-[#C9A961]"}`}>
                            Wines
                        </NavLink>
                        <NavLink to="/wineries" data-testid="nav-wineries" className={({isActive}) => `tracking-wide transition-colors ${isActive ? "text-[#C9A961]" : "hover:text-[#C9A961]"}`}>
                            Wineries
                        </NavLink>
                        <NavLink to="/about" data-testid="nav-about" className={({isActive}) => `tracking-wide transition-colors ${isActive ? "text-[#C9A961]" : "hover:text-[#C9A961]"}`}>
                            About
                        </NavLink>
                        <NavLink to="/contact" data-testid="nav-contact" className={({isActive}) => `tracking-wide transition-colors ${isActive ? "text-[#C9A961]" : "hover:text-[#C9A961]"}`}>
                            Contact
                        </NavLink>
                        {dashboardLink() && (
                            <NavLink to={dashboardLink()} data-testid="nav-dashboard" className={({isActive}) => `tracking-wide transition-colors ${isActive ? "text-[#C9A961]" : "hover:text-[#C9A961]"}`}>
                                Dashboard
                            </NavLink>
                        )}
                        {user ? (
                            <button onClick={handleLogout} data-testid="nav-logout" className="flex items-center gap-2 text-sm tracking-wide hover:text-[#C9A961] transition-colors">
                                <LogOut className="w-4 h-4" /> Logout
                            </button>
                        ) : (
                            <>
                                <Link to="/login" data-testid="nav-login" className="tracking-wide hover:text-[#C9A961] transition-colors">Login</Link>
                                <Link to="/register" data-testid="nav-register" className="btn-gold !py-3 !px-6">Join as Winery</Link>
                            </>
                        )}
                    </nav>

                    <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden bg-black border-t border-[#27272A] px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
                        <Link to="/wines" onClick={() => setOpen(false)} className="text-base">Wines</Link>
                        <Link to="/wineries" onClick={() => setOpen(false)} className="text-base">Wineries</Link>
                        <Link to="/about" onClick={() => setOpen(false)} className="text-base" data-testid="nav-mobile-about">About</Link>
                        <Link to="/contact" onClick={() => setOpen(false)} className="text-base" data-testid="nav-mobile-contact">Contact</Link>
                        {dashboardLink() && <Link to={dashboardLink()} onClick={() => setOpen(false)} className="text-base">Dashboard</Link>}
                        {user ? (
                            <button onClick={() => { setOpen(false); handleLogout(); }} className="text-left text-base">Logout</button>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="text-base">Login</Link>
                                <Link to="/register" onClick={() => setOpen(false)} className="btn-gold !py-3 !px-6 self-start">Join as Winery</Link>
                            </>
                        )}
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            <footer className="border-t border-[#27272A] py-16 px-6 md:px-12 lg:px-16 mt-32 bg-black">
                <div className="grid md:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <Wine className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                            <span className="font-display text-2xl">CellarGlobal</span>
                        </div>
                        <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-sm">
                            The premium B2B marketplace connecting Argentina's finest wineries directly with global importers.
                        </p>
                    </div>
                    <div>
                        <p className="overline mb-4">Marketplace</p>
                        <ul className="space-y-2 text-sm text-[#A1A1AA]">
                            <li><Link to="/wines" className="hover:text-[#C9A961]">All Wines</Link></li>
                            <li><Link to="/wineries" className="hover:text-[#C9A961]">All Wineries</Link></li>
                            <li><Link to="/about" className="hover:text-[#C9A961]" data-testid="footer-about">About CellarGlobal</Link></li>
                            <li><Link to="/contact" className="hover:text-[#C9A961]" data-testid="footer-contact">Contact</Link></li>
                            <li><Link to="/register" className="hover:text-[#C9A961]">List your Winery</Link></li>
                        </ul>
                    </div>
                    <div>
                        <p className="overline mb-4">Origin</p>
                        <p className="text-sm text-[#A1A1AA]">Mendoza, Argentina</p>
                        <p className="text-sm text-[#A1A1AA] mt-2">© {new Date().getFullYear()} CellarGlobal</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
